package com.videoanalyzer.web.components

import androidx.compose.runtime.*
import com.videoanalyzer.web.components.auth.*

/**
 * Auth page type for navigation.
 */
enum class AuthPage {
    Landing,
    SignIn,
    SignUp,
    ForgotPassword,
    ResetPassword,
    TermsOfService,
    PrivacyPolicy,
    Contact
}

/**
 * Main auth container that manages landing, sign-in, and sign-up pages.
 *
 * Component implementations are in the auth/ subpackage:
 * - LandingPage.kt: Landing page with value proposition
 * - AuthPages.kt: SignIn, SignUp, ForgotPassword, ResetPassword
 * - LegalPages.kt: Terms, Privacy, Contact pages
 * - SharedAuthComponents.kt: Shared layouts and UI components
 */
@Composable
fun AuthScreen(
    onSignInWithGoogle: () -> Unit,
    onSignInWithEmail: (email: String, password: String) -> Unit,
    onSignUpWithEmail: (email: String, password: String) -> Unit,
    onForgotPassword: (email: String) -> Unit = {},
    onResetPassword: (newPassword: String) -> Unit = {},
    isLoading: Boolean = false,
    errorMessage: String? = null,
    successMessage: String? = null,
    initialPage: AuthPage = AuthPage.Landing,
    recoveryToken: String? = null
) {
    var currentPage by remember { mutableStateOf(initialPage) }

    // If we have a recovery token, go to reset password page
    LaunchedEffect(recoveryToken) {
        if (recoveryToken != null) {
            currentPage = AuthPage.ResetPassword
        }
    }

    when (currentPage) {
        AuthPage.Landing -> {
            LandingPage(
                onSignIn = { currentPage = AuthPage.SignIn },
                onSignUp = { currentPage = AuthPage.SignUp },
                onTermsOfService = { currentPage = AuthPage.TermsOfService },
                onPrivacyPolicy = { currentPage = AuthPage.PrivacyPolicy },
                onContact = { currentPage = AuthPage.Contact }
            )
        }
        AuthPage.SignIn -> {
            SignInPage(
                onSignInWithGoogle = onSignInWithGoogle,
                onSignInWithEmail = onSignInWithEmail,
                onGoToSignUp = { currentPage = AuthPage.SignUp },
                onGoToForgotPassword = { currentPage = AuthPage.ForgotPassword },
                onBack = { currentPage = AuthPage.Landing },
                onTermsOfService = { currentPage = AuthPage.TermsOfService },
                onPrivacyPolicy = { currentPage = AuthPage.PrivacyPolicy },
                isLoading = isLoading,
                errorMessage = errorMessage,
                successMessage = successMessage
            )
        }
        AuthPage.SignUp -> {
            SignUpPage(
                onSignInWithGoogle = onSignInWithGoogle,
                onSignUpWithEmail = onSignUpWithEmail,
                onGoToSignIn = { currentPage = AuthPage.SignIn },
                onBack = { currentPage = AuthPage.Landing },
                onTermsOfService = { currentPage = AuthPage.TermsOfService },
                onPrivacyPolicy = { currentPage = AuthPage.PrivacyPolicy },
                isLoading = isLoading,
                errorMessage = errorMessage,
                successMessage = successMessage
            )
        }
        AuthPage.ForgotPassword -> {
            ForgotPasswordPage(
                onSubmit = onForgotPassword,
                onBack = { currentPage = AuthPage.SignIn },
                isLoading = isLoading,
                errorMessage = errorMessage,
                successMessage = successMessage
            )
        }
        AuthPage.ResetPassword -> {
            ResetPasswordPage(
                onSubmit = onResetPassword,
                onBack = { currentPage = AuthPage.SignIn },
                isLoading = isLoading,
                errorMessage = errorMessage,
                successMessage = successMessage
            )
        }
        AuthPage.TermsOfService -> {
            TermsOfServicePage(
                onBack = { currentPage = AuthPage.Landing }
            )
        }
        AuthPage.PrivacyPolicy -> {
            PrivacyPolicyPage(
                onBack = { currentPage = AuthPage.Landing }
            )
        }
        AuthPage.Contact -> {
            ContactPage(
                onBack = { currentPage = AuthPage.Landing }
            )
        }
    }
}
